import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import { useMemo, useState, type ReactNode } from 'react';

import { useAdGroups } from '@/api/ad';
import {
  AddOptionRow,
  ComboboxChip,
  ComboboxChips,
  ComboboxChipsInput,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxItem,
  ComboboxStatus,
  ComboboxValue,
  DebouncedCombobox,
  InfiniteComboboxList,
  useAddableCombobox,
  type ComboboxProps,
} from '@/components/Form/pickers';

const PAGE_SIZE = 50;

type Props = Omit<
  ComboboxProps<string, true>,
  'items' | 'multiple' | 'value' | 'onValueChange' | 'itemToStringValue' | 'itemToStringLabel'
> & {
  className?: string;
  disabled?: boolean;
  notFoundContent?: ReactNode;
  placeholder?: string;
  value?: string[];
  isLoading?: boolean;
  channelId?: string;
};

const AdGroupNameMultiSelect = ({
  value,
  isLoading: isLoadingProp,
  className,
  disabled = false,
  notFoundContent: notFoundContentProp,
  size = 'large',
  placeholder: placeholderProp,
  channelId,
  'aria-invalid': ariaInvalid,
  ...rest
}: Props) => {
  const notFoundContent = notFoundContentProp ?? <Trans>No ad groups were found...</Trans>;
  const placeholder = placeholderProp ?? t`Select Ad Groups...`;
  const [search, setSearch] = useState('');
  const [inputSearch, setInputSearch] = useState('');
  const { adGroupOptions, hasMore, isLoading, isFetchingNextPage, fetchNextPage } = useAdGroups({
    search,
    pageSize: PAGE_SIZE,
    channelId,
  });

  const items = useMemo(
    () =>
      Array.from(
        new Set(
          (adGroupOptions ?? [])
            .map((option) => option.name ?? '')
            .filter((name) => name.length > 0),
        ),
      ),
    [adGroupOptions],
  );

  const { isAddOption, isAddOptionSelected, itemsWithAddOption } = useAddableCombobox({
    inputSearch,
    value,
    items,
  });

  return (
    <DebouncedCombobox
      {...rest}
      data-testid="ad-group-multiselect"
      multiple
      filteredItems={itemsWithAddOption}
      isLoading={isLoadingProp || isLoading || isFetchingNextPage}
      disabled={disabled}
      size={size}
      value={value}
      onInputValueChange={(nextSearch) => setInputSearch(nextSearch)}
      onSearch={setSearch}
    >
      <ComboboxChips className={className} aria-invalid={ariaInvalid}>
        <ComboboxValue>
          {(value ?? []).map((item) => (
            <ComboboxChip key={item}>{item}</ComboboxChip>
          ))}
        </ComboboxValue>
        <ComboboxChipsInput placeholder={placeholder} />
      </ComboboxChips>
      <ComboboxContent>
        <ComboboxStatus />
        <ComboboxEmpty>{notFoundContent}</ComboboxEmpty>
        <InfiniteComboboxList
          fetchNextPage={fetchNextPage}
          hasMore={hasMore}
          isLoading={isLoading}
          isFetchingNextPage={isFetchingNextPage}
        >
          {(item: string) => (
            <ComboboxItem key={item} value={item}>
              {isAddOption(item) ? (
                <AddOptionRow label={item} isSelected={isAddOptionSelected} />
              ) : (
                item
              )}
            </ComboboxItem>
          )}
        </InfiniteComboboxList>
      </ComboboxContent>
    </DebouncedCombobox>
  );
};

export default AdGroupNameMultiSelect;
